// Stealth Clock — org-admin shift timeline DRAWER (the primary admin entry point).
//
// Slide-over from the "Shift Timeline" button on DashboardV2, matching the existing
// DetailDrawer pattern (portal → backdrop → spring from the right). Wider than the
// standard 560px drawer because each row carries a full-day rail.
//
// ADMIN ONLY. The button is gated by canUserAccessShiftTimeline (org flag + org_admin/
// main_admin) and the endpoint enforces the same independently, scoped to the caller's
// own organization — so an employee cannot reach anyone's timings but their own.
//
// Renders nothing at all while closed, and the panel only fetches when open.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CalendarClock } from 'lucide-react';
import { useDashTheme } from '../../pages/DashboardV2/hooks/useDashTheme';
import { ShiftTimelinePanel } from './ShiftTimelinePanel';

export function ShiftTimelineDrawer({ open, onClose }) {
  const t = useDashTheme();

  // Escape closes, and the page behind must not scroll while the drawer is up.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9998,
              background: 'rgba(8,10,18,0.5)',
              backdropFilter: 'blur(3px)',
            }}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Shift timeline"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={t.motion.spring.snappy}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              // Wider than DetailDrawer: every row renders a full-day rail.
              width: 'min(760px, 100vw)',
              background: t.c.bg,
              borderLeft: `1px solid ${t.c.border}`,
              boxShadow: t.shadow.lg,
              display: 'flex',
              flexDirection: 'column',
              fontFamily: t.font.family,
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: `${t.s[5]}px ${t.s[5]}px ${t.s[4]}px`,
                borderBottom: `1px solid ${t.c.border}`,
                background: t.c.surface,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: t.c.primarySoft,
                    color: t.c.primary,
                    flexShrink: 0,
                  }}
                >
                  <CalendarClock size={19} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.lg, fontWeight: 600, color: t.c.text }}>
                    Shift timeline
                  </div>
                  <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>
                    When your team marked themselves on and off duty
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                style={{
                  width: 32,
                  height: 32,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  border: 'none',
                  background: t.c.surfaceMuted,
                  color: t.c.textSecondary,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: t.s[5] }}>
              <ShiftTimelinePanel active={open} compact />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default ShiftTimelineDrawer;
